import React, { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography, useTheme } from "@mui/material";
import { CircleBox } from "@features/recommender/common";
import { LinearProgressBar } from "@rentablo/components";
import { toPercent } from "@helpers";
import { NoDataBlock } from "../../NoDataBlock";

interface Item {
  name: ReactNode;
  value: number;
}

interface Props {
  name: string;
  items?: Item[];
}

export const BreakdownTable = ({ name, items }: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <>
      <Typography variant="body4" color="textSecondary" mt={2} mb={6}>
        {t("recommender.compareView.donutCharts.fromFund", { name })}
      </Typography>
      <Box display="flex" flexDirection="column" gap={1}>
        {Boolean(!items?.length) && <NoDataBlock />}
        {Boolean(items?.length) &&
          items?.map((item, index) => (
            <Box
              key={index}
              display="flex"
              flexDirection="column"
              gap={2}
              borderTop={`1px solid ${theme.palette.border.secondary}`}
              borderBottom={
                index === items.length - 1
                  ? `1px solid ${theme.palette.border.secondary}`
                  : "none"
              }
              py={2}
              px={3}
            >
              <Box
                display="flex"
                flexDirection="row"
                alignItems="center"
                gap={1.5}
              >
                <CircleBox
                  sx={{
                    backgroundColor:
                      theme.palette.graphVisual[`0${index + 1}`]?.mid,
                    height: "14px",
                    width: "14px",
                  }}
                />
                <Typography variant="body1">{item.name}</Typography>
              </Box>
              <Box
                display="flex"
                flexDirection="row"
                alignItems="center"
                gap={2.5}
              >
                <LinearProgressBar
                  progress={item.value * 100}
                  thickness={8}
                  borderRadius={theme.spacing(1)}
                  bgColor={theme.palette.border.secondary}
                  color={theme.palette.primary.main}
                />
                <Typography variant="body3">{toPercent(item.value)}</Typography>
              </Box>
            </Box>
          ))}
      </Box>
    </>
  );
};
